import { map, tap, ignoreElements, filter, switchMap, withLatestFrom, take } from 'rxjs/operators';
import { combineEpics, Epic } from 'redux-observable';
import { Observable, zip, merge, interval, fromEvent, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import format from 'date-fns/format';

import { State } from '../rootReducer';
import { ASVPActions } from '../actions';
import { ApiProvider } from '../../api';
import { select } from '../../utils/operators';
import { setPlayerError } from '../reducers/playerReducer';
import {
    ContentEntryBuilder,
    Player,
    PlayerErrorResult,
    PlayerConfigBuilder,
    ComscoreConfig,
} from '@top/player-block-web';
import { MetadataState } from '../reducers/metadataReducer';
import { APIVideoType, getComscoreMediaType } from '../../tracking';
import {
    applyMuxToPlayBuilder,
    buildSetupMuxConfig,
    getMuxEnabledAtLastPlayerSetup,
} from '../../utils/muxPlayConfig';
import { muxDebugLog } from '../../utils/muxDebug';
import { buildTopPlayerSetupConfig, TopPlayerOptions } from '../../player/TopPlayer';

//
// ─── TYPINGS ────────────────────────────────────────────────────────────────────
//

export interface PlayerEventsEpicDependencies {
    auth: any;
    player: Player;
    /** Needed to re-run `player.setup` when Mux becomes eligible (Tub creates Mux only at setup). */
    topPlayerOptions: TopPlayerOptions;
    videoData?: ApiProvider;
}

type PlayerEventsEpic = Epic<ASVPActions, ASVPActions, State, PlayerEventsEpicDependencies>;

//
// ─── EPIC ───────────────────────────────────────────────────────────────────────
//

// const playerEventsEpic: PlayerEventsEpic = (action$, state$, { player, videoData = new VideoService(), auth }) => {
//     console.log(player, state$, auth);

//     return merge(
//         fromEventPattern((h) => player.events.mediaTimeChanged.listen(h)).pipe(map((r) => ({type: 'ACTION!!', payload: 'fuck'}))),
//         fromEventPattern((h) => player.events.playerReady.listen(h)).pipe(
//             map(() => {}),
//             ignoreElements()
//         ),
//         fromEventPattern((h) => player.events.mediaLoaded.listen(h)).pipe(mapTo(mediaLoaded(player.model))),
//         fromEventPattern((h) => player.events.mediaReady.listen(h)).pipe(
//             switchMap(() => videoData.request<VideoDataResponse>('/r0xw2u30R3GchW27yVqlug?' + serialize(params)).pipe(
//                 map(json => {
//                     console.log(json.data)
//                     return { type: 'Loaded!', payload: json.data }
//                 }),
//                 catchError((err) => of({ type: 'ERROR', payload: err }))
//         ))),
//     )}

function buildComscoreConfig(metadata: MetadataState, player: Player): ComscoreConfig {

    const lastAirDate = metadata.launchDate
                        ? format(new Date(metadata.launchDate * 1000), 'YYYY-MM-DD HH:mm:ss')
                        : undefined;

    return {
        enabled: true,
        publisherId: "6035748",
        applicationName: "TOP",
        applicationVersion: player.config.metadata.playerVersion ?? "",
        isOTT: false,
        debug: false,
        childDirectedAppMode: false,
        mediaType: getComscoreMediaType(metadata.videoType),
        uniqueContentId: metadata.mediaID,
        publisherBrandName: "Adult Swim",
        programTitle: metadata.collectionTitle,
        programId: metadata.videoID,
        episodeTitle: metadata.title,
        episodeId: metadata.titleID,
        episodeSeasonNumber: metadata.seasonNumber?.toString(),
        episodeNumber: metadata.episodeNumber?.toString(),
        contentGenre: "*null",
        advertisementLoad: true,
        digitalAirdate: lastAirDate,
        tvAirdate: lastAirDate,
        stationTitle: "Adult Swim",
        c3: "*null",
        c4: "ADULTSWIM",
        c6: "*null",
        completeEpisode: metadata.videoType === APIVideoType.EPISODE,
        feedType: metadata.videoType
    }
}

// when auth token and media id have been updated, use it to play the media
const playbackAuthWithMediaID: PlayerEventsEpic = (action$, state$, { player }) => {
    return zip( // get token and mediaID
        state$.pipe(select(state => state.auth.token)),
        state$.pipe(select(state => state.metadata.mediaID)),
    ).pipe(
        filter(([token, mediaID]) => !!token && !!mediaID), // if token and mediaID are truthy
        switchMap(([token, mediaID]) => state$.pipe(
            withLatestFrom(
                state$.pipe(map(state => state)),
            ),
            filter(([, state]) => !!state.auth.isReady && state.metadata.isAuth),
            take(1), // only do this once per token/mediaID combo
            tap(([, state]) => { // play content via mediaID with token
                // console.log('playbackAuthWithMediaID', state)

                const playConfig = applyMuxToPlayBuilder(
                    PlayerConfigBuilder.forPlay()
                        .withUI({ posterImage: state.metadata.poster })
                        .withComscore(buildComscoreConfig(state.metadata, player))
                        .withFeatures({
                            ccpa: {
                                doNotSell: !(<any>window)?.WM?.UserConsent?.getUserConsentAdvertisingState() // Inverted because Consent false === doNotSell true
                            }
                        }),
                    state.metadata,
                    player,
                    state.auth
                ).build();

                player.updateConfig(playConfig)

                const playOptions = ContentEntryBuilder.forEntryOptions()
                    .withAccessToken(token)
                    .withAccessTokenType('adobe')
                    .withPlayConfigOverrides(playConfig)
                    .build();

                player.playByMediaJson({ mediaId: mediaID }, playOptions);
            }),
            ignoreElements()
        ))
    );
};

const playbackUnauthWithMediaID: PlayerEventsEpic = (action$, state$, { player }) => {
    return zip(
        state$.pipe(select(state => state.metadata.mediaID)),
    ).pipe(
        filter(([mediaID]) => !!mediaID),
        switchMap(mediaID => state$.pipe(
            withLatestFrom(
                state$.pipe(map(state => state)),
            ),
            filter(([, state]) =>{
            return !!state.auth.isReady && !state.metadata.isAuth}),
            take(1),
            tap(([, state]) => {
                // console.log('playbackUnauthWithMediaID', state)

                const playConfig = applyMuxToPlayBuilder(
                    PlayerConfigBuilder.forPlay()
                        .withUI({ posterImage: state.metadata.poster })
                        .withComscore(buildComscoreConfig(state.metadata, player))
                        .withFeatures({
                            ccpa: {
                                doNotSell: !(<any>window)?.WM?.UserConsent?.getUserConsentAdvertisingState() // Inverted because Consent false === doNotSell true
                            }
                        }),
                    state.metadata,
                    player,
                    state.auth
                ).build();

                player.updateConfig(playConfig)

                const playOptions = ContentEntryBuilder.forEntryOptions()
                        .withPlayConfigOverrides(playConfig)
                        .build();

                player.playByMediaJson({ mediaId: state.metadata.mediaID }, playOptions);
            }),
            ignoreElements()
        ))
    );
};

const playbackNoAuthWithMediaID: PlayerEventsEpic = (action$, state$, { player }) => {
    return zip( // get token and mediaID
        state$.pipe(select(state => state.metadata.mediaID)),
    ).pipe(
        filter(([mediaID]) => !!mediaID),
        switchMap(mediaID => state$.pipe(
            withLatestFrom(
                state$.pipe(map(state => state)),
            ),
            filter(([, state]) => state.options.useAuthLib === false),
            take(1),
            tap(([, state]) => {
                // console.log('playbackNoAuthWithMediaID', state)

                const playConfig = applyMuxToPlayBuilder(
                    PlayerConfigBuilder.forPlay()
                        .withUI({ posterImage: state.metadata.poster })
                        .withComscore(buildComscoreConfig(state.metadata, player))
                        .withFeatures({
                            ccpa: {
                                doNotSell: !(<any>window)?.WM?.UserConsent?.getUserConsentAdvertisingState() // Inverted because Consent false === doNotSell true
                            }
                        }),
                    state.metadata,
                    player,
                    state.auth
                ).build();

                player.updateConfig(playConfig)

                const playOptions = ContentEntryBuilder.forEntryOptions()
                        .withPlayConfigOverrides(playConfig)
                        .build();

                player.playByMediaJson({ mediaId: state.metadata.mediaID }, playOptions);
            }),
            ignoreElements()
        ))
    );
};

// when playback url is set, use it to play back the media
const playbackWithoutMediaID: PlayerEventsEpic = (action$, state$, { player }) => {
    return zip( // get token and mediaID
        state$.pipe(select(state => state.metadata.playbackURL)),
    ).pipe(
        filter(([, playbackURL]) => !!playbackURL), // if truthy
        switchMap((playbackURL) => state$.pipe( // switch to getting the poster image
            withLatestFrom(
                state$.pipe(map(state => state)),
            ),
            filter(([, state]) => !state.metadata.isAuth && (!state.options.useAuthLib === false || !!state.auth.isReady)),
            take(1), // only do this once per token/mediaID combo
            tap(([, state]) => { // play content via mediaID with token
                // console.log('playbackWithoutMediaID', state)

                const playConfig = applyMuxToPlayBuilder(
                    PlayerConfigBuilder.forPlay()
                        .withUI({ posterImage: state.metadata.poster })
                        .withAds({ enabled: false })
                        .withComscore(buildComscoreConfig(state.metadata, player))
                        .withFeatures({
                            ccpa: {
                                doNotSell: !(<any>window)?.WM?.UserConsent?.getUserConsentAdvertisingState() // Inverted because Consent false === doNotSell true
                            }
                        }),
                    state.metadata,
                    player,
                    state.auth
                ).build();
                const playOptions = ContentEntryBuilder.forEntryOptions()
                    .withPlayConfigOverrides(playConfig)
                    .build();
                player.play(state.metadata.playbackURL, playOptions);
            }),
            ignoreElements()
        ))
    );
}


const onPlayerError: PlayerEventsEpic = (action$, state$, { player }) => {
    const playerModel$ = new Observable<PlayerErrorResult>(observer => {
        const off = player.events.playerError.listen((error: PlayerErrorResult) => {
            console.error("playerError", error);
            observer.next(error);
        });
        return () => {
            player.events.playerError.unlisten(off);
        };
    });

    return playerModel$.pipe(map(setPlayerError));
}

/**
 * Tub only instantiates `muxReporter` when `tubConfiguration.mux.enabled` is true at engine setup.
 * `updateConfig` cannot add Mux later. When consent + key become available after init (e.g. WBD
 * was not ready on first tick), re-run `player.setup` with the same full config as TopPlayer.
 *
 * Transition uses {@link getMuxEnabledAtLastPlayerSetup} (what was last passed to `setup`) vs
 * current `buildSetupMuxConfig().enabled`, so `syncMuxWithConsent` after store bootstrap does not
 * cause a duplicate `setup` on the next tick.
 *
 * `ackTermsChanged` (document) — fired when WBD LTP / UCL acknowledges terms (e.g. Adult Swim LTP
 * modal); re-check immediately instead of waiting on interval(100).
 */
const applyMuxWhenConsentReadyEpic: PlayerEventsEpic = (action$, state$, { player, topPlayerOptions }) => {
    const oneTrust$ =
        typeof window !== 'undefined'
            ? fromEvent(window, 'OneTrustGroupsUpdated').pipe(catchError(() => EMPTY))
            : EMPTY;
    const ackTermsChanged$ =
        typeof document !== 'undefined'
            ? fromEvent(document, 'ackTermsChanged').pipe(catchError(() => EMPTY))
            : EMPTY;
    return merge(state$, interval(100), oneTrust$, ackTermsChanged$).pipe(
        map(() => {
            const cur = buildSetupMuxConfig().enabled === true;
            return !getMuxEnabledAtLastPlayerSetup() && cur;
        }),
        filter(Boolean),
        take(1),
        tap(() => {
            muxDebugLog(
                'applyMuxWhenConsentReadyEpic: re-running player.setup (consent became ready for Mux)'
            );
            player.setup(buildTopPlayerSetupConfig(topPlayerOptions));
        }),
        ignoreElements()
    );
};

const Epics = combineEpics(
    // playerEventsEpic,
    playbackAuthWithMediaID,
    playbackUnauthWithMediaID,
    playbackNoAuthWithMediaID,
    playbackWithoutMediaID,
    applyMuxWhenConsentReadyEpic,
    onPlayerError,
);

export default Epics;
